// Copyright ©️ 2026 eVolve MEP, LLC

import React, { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  type TaskType,
  type TaskTypeGroup,
  type ShopConfiguration,
  useEvolvePaginatedGet,
  type WrappedPaginatedGet,
} from '@evolvemep/api-ts';

import type { UseState } from 'helpers/helper-types';
import { useGeneralContext } from 'helpers/useGeneralContext';

type TasksContextType = {
  selectedTasks: TaskType[];
  setSelectedTasks: UseState<TaskType[]>;
  selectedGroups: TaskTypeGroup[];
  setSelectedGroups: UseState<TaskTypeGroup[]>;
  onTaskSelect: (t: TaskType) => void;
  onGroupSelect: (g: TaskTypeGroup) => void;
  searchText: string | undefined;
  setSearchText: UseState<string | undefined>;
  groupsPaginatedGet: WrappedPaginatedGet<'shop/taskTypeGroup'>;
  hideSelect: boolean;
  setHideSelect: UseState<boolean>;
  shopConfiguration: ShopConfiguration | undefined;
  setShopConfiguration: UseState<ShopConfiguration | undefined>;
  workCellSetup: boolean;
};

const TaskTypesContext = createContext<TasksContextType | undefined>(undefined);

export const TaskTypesProvider = ({
  children,
  workCellSetup = false,
}: {
  children: ReactNode;
  workCellSetup?: boolean;
}) => {
  const [selectedTasks, setSelectedTasks] = useState<TaskType[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<TaskTypeGroup[]>([]);
  const [searchText, setSearchText] = useState<string | undefined>();
  const [hideSelect, setHideSelect] = useState(false);
  const [shopConfiguration, setShopConfiguration] = useState<ShopConfiguration>();

  const groupsPaginatedGet = useEvolvePaginatedGet('shop/taskTypeGroup');

  useEffect(() => {
    if (hideSelect) {
      setSelectedTasks([]);
      setSelectedGroups([]);
    }
  }, [hideSelect]);

  const onTaskSelect = useCallback(
    (t: TaskType) => {
      setSelectedTasks((prevOptions) => {
        const newOptions = [...prevOptions];
        const indexOf = prevOptions.findIndex((o: TaskType) => o.taskTypeId === t.taskTypeId);
        if (indexOf >= 0) {
          newOptions.splice(indexOf, 1);
        } else {
          newOptions.push(t);
        }
        return newOptions;
      });
    },
    [setSelectedTasks],
  );

  const onGroupSelect = useCallback(
    (g: TaskTypeGroup) => {
      setSelectedGroups((prevOptions) => {
        const newGroupList = [...prevOptions];
        const indexOfGroup = prevOptions.findIndex((o: TaskTypeGroup) => o.taskTypeGroupId === g.taskTypeGroupId);
        if (indexOfGroup >= 0) {
          newGroupList.splice(indexOfGroup, 1);
        } else {
          newGroupList.push(g);
        }
        return newGroupList;
      });
    },
    [setSelectedGroups],
  );

  const taskTypesObject = useMemo<TasksContextType>(
    () => ({
      selectedTasks,
      setSelectedTasks,
      selectedGroups,
      setSelectedGroups,
      onTaskSelect,
      onGroupSelect,
      searchText,
      setSearchText,
      groupsPaginatedGet,
      hideSelect,
      setHideSelect,
      shopConfiguration,
      setShopConfiguration,
      workCellSetup,
    }),
    [
      selectedTasks,
      selectedGroups,
      onTaskSelect,
      onGroupSelect,
      searchText,
      groupsPaginatedGet,
      hideSelect,
      shopConfiguration,
      workCellSetup,
    ],
  );

  return <TaskTypesContext value={taskTypesObject}>{children}</TaskTypesContext>;
};

export const useTaskTypes = () => useGeneralContext(TaskTypesContext, 'TaskTypes');
